import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Autocomplete,
  Avatar,
} from "@mui/material";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { reportViewToggleButtonSx, reportViewToggleGroupSx } from "../OtherMasters/Reports/reportViewToggleStyles";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";

import Layout from "../../layout/Layout";
import { apiData } from "../../services/api_service";
import { fetchSalesTargetReportAPI, fetchMonthwiseSalesTargetReportAPI, fetchSalesPersonsAPI } from "../../config";
import { formatAED } from "../../utils/format";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

// Modern Shiny Shimmer Skeleton Component
const ShinySkeleton = ({ width, height, borderRadius = 6, style = {} }) => (
  <Box
    sx={{
      width: width || "100%",
      height: height || 20,
      borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 35%, #ffffff 50%, #f8fafc 65%, #e2e8f0 100%)",
      backgroundSize: "400% 100%",
      animation: "shinyShimmer 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      "@keyframes shinyShimmer": {
        "0%": { backgroundPosition: "100% 0%" },
        "100%": { backgroundPosition: "0% 0%" },
      },
      ...style,
    }}
  />
);

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

const statusOptions = [
  { value: "ALL", label: "All Statuses" },
  { value: "Achieved", label: "Achieved (>= 100%)" },
  { value: "On Track", label: "On Track (75% - 99%)" },
  { value: "Lagging", label: "Lagging (< 75%)" },
  { value: "No Target", label: "No Target set" },
];

const SalesTargetReport = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0: Selected Month Report, 1: Month-wise Breakdown
  const [loading, setLoading] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const loggedUserId = userData?.emp_id || userData?.id || userData?.user_id;
  const isAdmin =
    Number(userData?.role_id || userData?.position_id || userData?.position) === 377 ||
    Number(userData?.role_id) === 1 ||
    String(userData?.role_name || userData?.role || "").toLowerCase().includes("admin") ||
    String(userData?.position_name || userData?.position || "").toLowerCase().includes("admin") ||
    userData?.user_type === "admin";

  const defaultSalesPersonId = !isAdmin && loggedUserId ? String(loggedUserId) : "";

  // Controlled Filter Form State (User inputs)
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [salesPersonId, setSalesPersonId] = useState(defaultSalesPersonId);
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");

  // Applied Filter State (Only updated when Search / Clear is clicked or Tab changes)
  const [appliedFilters, setAppliedFilters] = useState({
    year: currentYear,
    month: new Date().getMonth() + 1,
    salesPersonId: defaultSalesPersonId,
    status: "ALL",
    search: "",
  });

  // Report Data
  const [reportData, setReportData] = useState(null);
  const [monthwiseData, setMonthwiseData] = useState([]);
  const [salesPersonsList, setSalesPersonsList] = useState([]);

  const fetchSalesPersons = async () => {
    try {
      const res = await apiData("get", fetchSalesPersonsAPI);
      const list = res?.data?.data || res?.data || [];
      if (Array.isArray(list)) {
        setSalesPersonsList(list);
      }
    } catch (err) {
      console.error("Error fetching sales persons list:", err);
    }
  };

  useEffect(() => {
    fetchSalesPersons();
  }, []);

  // Fetch Report Data using appliedFilters
  const fetchReport = async (filtersToUse = appliedFilters) => {
    try {
      setLoading(true);
      if (activeTab === 0) {
        const payload = {
          year: filtersToUse.year,
          month: filtersToUse.month,
          sales_person_id: filtersToUse.salesPersonId || null,
          status: filtersToUse.status !== "ALL" ? filtersToUse.status : null,
          search: filtersToUse.search || null,
        };
        const res = await apiData("get", fetchSalesTargetReportAPI, payload);
        const data = res?.data?.data || res?.data || {};
        setReportData(data);

        if (salesPersonsList.length === 0 && Array.isArray(data.report)) {
          const list = data.report.map((item) => ({
            sales_person_id: item.sales_person_id,
            employee_name: item.employee_name,
            employee_no: item.employee_no,
          }));
          setSalesPersonsList(list);
        }
      } else {
        const payload = {
          year: filtersToUse.year,
          sales_person_id: filtersToUse.salesPersonId || null,
          search: filtersToUse.search || null,
        };
        const res = await apiData("get", fetchMonthwiseSalesTargetReportAPI, payload);
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.monthwise_report)
            ? res.data.monthwise_report
            : Array.isArray(res?.data?.data)
              ? res.data.data
              : [];
        setMonthwiseData(list);
      }
    } catch (err) {
      console.error("Fetch Sales Target Report Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch report ONLY when activeTab or appliedFilters changes
  useEffect(() => {
    fetchReport(appliedFilters);
  }, [activeTab, appliedFilters]);

  // Execute Search ONLY on explicit Search button click / form submit
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const newFilters = {
      year,
      month,
      salesPersonId,
      status,
      search,
    };
    setAppliedFilters(newFilters);
  };

  // Reset inputs AND trigger search update on Clear button click
  const handleClearFilters = () => {
    const defaultFilters = {
      year: currentYear,
      month: new Date().getMonth() + 1,
      salesPersonId: defaultSalesPersonId,
      status: "ALL",
      search: "",
    };
    setYear(defaultFilters.year);
    setMonth(defaultFilters.month);
    setSalesPersonId(defaultFilters.salesPersonId);
    setStatus(defaultFilters.status);
    setSearch(defaultFilters.search);
    setAppliedFilters(defaultFilters);
  };

  // `table` (when given) is the table on screen: export its rows after any search / column filter.
  const exportMonthlyReportToExcel = (table) => {
    if (activeTab === 0 && reportData?.report) {
      const reportRowsForExport = table && typeof table.getPrePaginationRowModel === "function" ? table.getPrePaginationRowModel().rows.map((r) => r.original) : reportData.report;
      const isFilteredView = reportRowsForExport.length !== reportData.report.length;
      const excelData = reportRowsForExport.map((r) => ({
        "Salesperson ID": r.sales_person_id,
        "Salesperson Name": r.employee_name,
        "Employee No": r.employee_no || "-",
        "Position Name": r.position_name || "-",
        Year: r.year,
        Month: monthOptions.find((m) => m.value === r.month)?.label || r.month,
        "Target Amount (AED)": r.target_amount,
        "Target Source": r.target_source === "explicit" ? "Explicit" : r.target_source === "inherited" ? `Inherited (${r.inherited_period})` : "Default",
        "Actual Sales (AED)": r.actual_sales,
        "Paid Collections (AED)": r.paid_collections,
        "Achievement (%)": r.status === "No Target" ? "" : Number(r.achievement_percentage) || 0,
        "Shortfall / Surplus (AED)": r.status === "No Target" ? "" : Number(r.variance) || 0,
        "Total Orders": r.actual_orders,
        Status: r.status,
      }));
      // Totals row, matching the summary cards shown on screen. The screen's
      // Actual Sales / Paid Collections / Orders count only reps WITH a target
      // (so achievement is measured against the same people), so this row is
      // labelled accordingly and a second row gives the all-reps sums of the
      // rows above (the screen shows that separately as "All reps' sales").
      if (!isFilteredView) {
        excelData.push({
          "Salesperson ID": "",
          "Salesperson Name": "TOTAL (reps with a target only)",
          "Target Amount (AED)": summary.total_target_amount || 0,
          "Actual Sales (AED)": summary.total_actual_sales || 0,
          "Paid Collections (AED)": summary.total_paid_collections || 0,
          "Achievement (%)": Number(summary.total_target_amount) > 0 ? Number(summary.overall_achievement_percentage) || 0 : "",
          "Total Orders": summary.total_actual_orders || 0,
        });
      }
      {
        const allSum = (k) =>
          reportRowsForExport.reduce((a, r) => a + (Number(r[k]) || 0), 0);
        excelData.push({
          "Salesperson ID": "",
          "Salesperson Name": isFilteredView ? `FILTERED REPS (${reportRowsForExport.length}) - sum of rows above` : `ALL REPS (${reportRowsForExport.length}) - sum of rows above`,
          "Target Amount (AED)": allSum("target_amount"),
          "Actual Sales (AED)": allSum("actual_sales"),
          "Paid Collections (AED)": allSum("paid_collections"),
          "Total Orders": allSum("actual_orders"),
        });
      }

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      fitColumns(worksheet);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Target vs Actual");
      writeXlsxFrozen(XLSX, workbook, `Sales_Target_Report_${year}_Month_${month}.xlsx`);
    } else if (activeTab === 1 && monthwiseData.length > 0) {
      const rows = [];
      monthwiseData.forEach((sp) => {
        sp.monthly_breakdown.forEach((mb) => {
          rows.push({
            "Salesperson Name": sp.employee_name,
            "Employee No": sp.employee_no || "-",
            Year: sp.year,
            Month: mb.month_name,
            "Target Amount (AED)": mb.target_amount,
            "Target Source": mb.target_source,
            "Actual Sales (AED)": mb.actual_sales,
            "Achievement (%)": Number(mb.target_amount) > 0 ? Number(mb.achievement_percentage) || 0 : "",
            "Variance (AED)": Number(mb.target_amount) > 0 ? Number(mb.variance) || 0 : "",
            "Actual Orders": mb.actual_orders,
          });
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      fitColumns(worksheet);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Monthwise Performance");
      writeXlsxFrozen(XLSX, workbook, `Monthwise_Sales_Target_Report_${year}.xlsx`);
    }
  };

  const columnsMonthly = useMemo(
    () => [
      {
        accessorKey: "employee_name",
        header: "Sales Representative",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0284c7", fontWeight: 700, width: 36, height: 36, fontSize: "0.9rem" }}>
              {row.original.employee_name?.charAt(0) || "S"}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {row.original.employee_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Code: {row.original.employee_no || "N/A"} | {row.original.position_name || "Sales Executive"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: "target_amount",
        header: "Target Amount",
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: "#334155" }}>
              {formatAED(row.original.target_amount)}
            </Typography>
            <Chip
              label={
                row.original.target_source === "explicit"
                  ? "Explicit"
                  : row.original.target_source === "inherited"
                    ? `Past (${row.original.inherited_period})`
                    : "Not Set"
              }
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 18, fontWeight: 600, mt: 0.3 }}
              color={row.original.target_source === "explicit" ? "primary" : row.original.target_source === "inherited" ? "warning" : "default"}
            />
          </Box>
        ),
      },
      {
        accessorKey: "actual_sales",
        header: "Actual Achieved",
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: "#0284c7" }}>
              {formatAED(row.original.actual_sales)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Paid: {formatAED(row.original.paid_collections)}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "achievement_percentage",
        header: "Achievement %",
        Cell: ({ row }) => {
          if (row.original.status === "No Target") {
            return <Typography variant="body2" color="text.secondary">—</Typography>;
          }
          const pct = row.original.achievement_percentage || 0;
          const color = pct >= 100 ? "#16a34a" : pct >= 75 ? "#d97706" : "#dc2626";
          const progressVal = Math.min(100, pct);
          return (
            <Box sx={{ width: 140 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 800, color }}>
                  {pct}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {row.original.actual_orders} Orders
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressVal}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
                }}
              />
            </Box>
          );
        },
      },
      {
        accessorKey: "variance",
        header: "Shortfall / Surplus",
        Cell: ({ cell, row }) => {
          if (row.original.status === "No Target") {
            return <Typography variant="body2" color="text.secondary">—</Typography>;
          }
          const val = cell.getValue();
          const isSurplus = val >= 0;
          return (
            <Typography variant="body2" sx={{ fontWeight: 800, color: isSurplus ? "#16a34a" : "#dc2626" }}>
              {isSurplus ? `+ ${formatAED(val)}` : `- ${formatAED(Math.abs(val))}`}
            </Typography>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const st = cell.getValue();
          if (st === "Achieved") {
            return <Chip icon={<CheckCircleOutlineIcon />} label="Achieved" color="success" size="small" sx={{ fontWeight: 700, px: 0.5 }} />;
          } else if (st === "On Track") {
            return <Chip icon={<WarningAmberIcon />} label="On Track" color="warning" size="small" sx={{ fontWeight: 700, px: 0.5 }} />;
          }
          if (st === "No Target") {
            return <Chip label="No Target" size="small" variant="outlined" sx={{ fontWeight: 700, px: 0.5, color: "#64748b", borderColor: "#cbd5e1" }} />;
          }
          return <Chip icon={<HighlightOffIcon />} label="Lagging" color="error" size="small" sx={{ fontWeight: 700, px: 0.5 }} />;
        },
      },
    ],
    []
  );

  const handleTabChange = (e, val) => {
    setActiveTab(val);
    const defaultFilters = {
      year: currentYear,
      month: new Date().getMonth() + 1,
      salesPersonId: defaultSalesPersonId,
      status: "ALL",
      search: "",
    };
    setYear(defaultFilters.year);
    setMonth(defaultFilters.month);
    setSalesPersonId(defaultFilters.salesPersonId);
    setStatus(defaultFilters.status);
    setSearch(defaultFilters.search);
    setAppliedFilters(defaultFilters);
  };

  const summary = reportData?.summary || {};

  return (
    <Layout>
      <Box className="employee-form bold-header">
        {/* Header — same layout as the other report pages */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography className="page_title" textAlign="left">
              Sales Target Performance Report
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
              <Link color="inherit" href="/dashboard" underline="hover">
                Dashboard
              </Link>
              {isAdmin && (
                <Link color="inherit" href="/sales-targets" underline="hover">
                  Sales Targets
                </Link>
              )}
              <Typography color="text.primary">Sales Target Report</Typography>
            </Breadcrumbs>
          </Box>

          <Stack direction="row" spacing={1.5}>
            {activeTab === 1 && (
              <Button onClick={() => exportMonthlyReportToExcel()} startIcon={<FileDownloadIcon />} sx={{ background: "#1a73e829", textTransform: "none" }}>
                Export To Excel
              </Button>
            )}
            {isAdmin && (
              <Button variant="contained" color="success" onClick={() => navigate("/sales-targets")}>
                Manage Targets
              </Button>
            )}
            <Button variant="contained" color="primary" onClick={() => navigate(-1)} startIcon={<ArrowBackIosIcon />}>
              Back
            </Button>
          </Stack>
        </Box>

        {/* View toggle — same control the Sales / Stock reports use */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            size="small"
            color="primary"
            sx={reportViewToggleGroupSx}
            onChange={(e, v) => v !== null && handleTabChange(e, v)}
          >
            <ToggleButton value={0} sx={reportViewToggleButtonSx}>
              Target vs Actual (Single Month)
            </ToggleButton>
            <ToggleButton value={1} sx={reportViewToggleButtonSx}>
              Month-wise Breakdown (Full Year)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Filter Bar */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 3.5, border: "1px solid #e2e8f0", borderRadius: 3, bgcolor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Select Year"
                  size="small"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value, 10))}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {activeTab === 0 && (
                <Grid item xs={12} sm={3} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Select Month"
                    size="small"
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                  >
                    {monthOptions.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {activeTab === 0 && (
                <Grid item xs={12} sm={3} md={2.5}>
                  <TextField
                    select
                    fullWidth
                    label="Performance Status"
                    size="small"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {statusOptions.map((st) => (
                      <MenuItem key={st.value} value={st.value}>
                        {st.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} sm={4} md={3}>
                <Autocomplete
                  size="small"
                  disabled={!isAdmin}
                  options={salesPersonsList}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    const name = option.employee_name || option.name || "";
                    const code = option.employee_no ? ` (${option.employee_no})` : "";
                    return `${name}${code}`.trim();
                  }}
                  value={
                    salesPersonsList.find(
                      (sp) => Number(sp.sales_person_id || sp.ID || sp.id) === Number(salesPersonId)
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setSalesPersonId(newValue ? newValue.sales_person_id || newValue.ID || newValue.id : "");
                  }}
                  isOptionEqualToValue={(option, val) =>
                    Number(option.sales_person_id || option.ID || option.id) === Number(val.sales_person_id || val.ID || val.id)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sales Person"
                      placeholder="All Sales Persons..."
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={2.5}>
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" size="medium" sx={{ height: 40, px: 3, fontWeight: 700 }}>
                    Search
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={handleClearFilters} sx={{ height: 40, px: 2 }}>
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Loading Shiny Skeleton State */}
        {loading ? (
          activeTab === 0 ? (
            <Box>
              <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Card elevation={0} sx={{ border: "1px solid #e2e8f0", p: 2.5, borderRadius: 3 }}>
                      <ShinySkeleton width="60%" height={14} borderRadius={4} style={{ marginBottom: 14 }} />
                      <ShinySkeleton width="85%" height={32} borderRadius={6} style={{ marginBottom: 10 }} />
                      <ShinySkeleton width="50%" height={14} borderRadius={4} />
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <ShinySkeleton key={i} height={48} borderRadius={6} style={{ marginBottom: 12 }} />
                ))}
              </Paper>
            </Box>
          ) : (
            <Box>
              {[1, 2].map((spIdx) => (
                <Paper key={spIdx} elevation={0} sx={{ p: 3, mb: 3.5, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ width: 220 }}>
                      <ShinySkeleton height={24} borderRadius={6} style={{ marginBottom: 8 }} />
                      <ShinySkeleton height={14} width={140} borderRadius={4} />
                    </Box>
                    <ShinySkeleton width={180} height={36} borderRadius={18} />
                  </Box>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((mIdx) => (
                      <Grid item xs={12} sm={6} md={3} lg={2} key={mIdx}>
                        <Card elevation={0} sx={{ border: "1px solid #e2e8f0", p: 2, borderRadius: 2 }}>
                          <ShinySkeleton height={16} width="60%" borderRadius={4} style={{ marginBottom: 8 }} />
                          <ShinySkeleton height={14} width="85%" borderRadius={4} style={{ marginBottom: 6 }} />
                          <ShinySkeleton height={16} width="95%" borderRadius={4} style={{ marginBottom: 10 }} />
                          <ShinySkeleton height={8} borderRadius={4} />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))}
            </Box>
          )
        ) : (
          <>
            {/* Tab 0: Single Month Performance Summary & Table */}
            {activeTab === 0 && (
              <>
                {/* Standard Dashboard Card Design */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {/* Card 1: Total Target Amount */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 1.8,
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#ffffff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease",
                        "&:hover": { boxShadow: "0 6px 20px rgba(15,23,42,0.06)", borderColor: "#cbd5e1" },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: "#4f46e5",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <TrackChangesIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography variant="caption" fontWeight="700" color="#64748b" textTransform="uppercase" letterSpacing="0.4px" sx={{ fontSize: "0.68rem" }}>
                          Total Target Amount
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        fontWeight="800"
                        color="#0f172a"
                        sx={{ fontSize: "1.1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", my: 0.5 }}
                      >
                        {formatAED(summary.total_target_amount || 0)}
                      </Typography>

                      <Typography variant="caption" fontWeight="500" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                        For <b>{summary.total_sales_persons || 0}</b> Sales Representatives
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Card 2: Actual Sales Achieved */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 1.8,
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#ffffff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease",
                        "&:hover": { boxShadow: "0 6px 20px rgba(15,23,42,0.06)", borderColor: "#cbd5e1" },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: "#0284c7",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <MonetizationOnIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography variant="caption" fontWeight="700" color="#64748b" textTransform="uppercase" letterSpacing="0.4px" sx={{ fontSize: "0.68rem" }}>
                          Actual Sales Achieved
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        fontWeight="800"
                        color="#0284c7"
                        sx={{ fontSize: "1.1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", my: 0.5 }}
                      >
                        {formatAED(summary.total_actual_sales || 0)}
                      </Typography>

                      <Typography variant="caption" fontWeight="500" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                        Paid Collections: <b>{formatAED(summary.total_paid_collections || 0)}</b>
                      </Typography>
                      <Typography variant="caption" display="block" color="#94a3b8" sx={{ fontSize: "0.64rem", mt: 0.4, lineHeight: 1.3 }}>
                        Counts only reps with a target set ({summary.reps_with_target || 0} of {summary.total_sales_persons || 0}).
                        {Number(summary.total_sales_all_reps) > Number(summary.total_actual_sales || 0) + 0.005 && (
                          <> All reps' sales this period: <b style={{ color: "#475569" }}>{formatAED(summary.total_sales_all_reps)}</b></>
                        )}
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Card 3: Overall Achievement % */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 1.8,
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#ffffff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease",
                        "&:hover": { boxShadow: "0 6px 20px rgba(15,23,42,0.06)", borderColor: "#cbd5e1" },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: summary.overall_achievement_percentage >= 100 ? "#16a34a" : summary.overall_achievement_percentage >= 75 ? "#eab308" : "#dc2626",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AssessmentIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography variant="caption" fontWeight="700" color="#64748b" textTransform="uppercase" letterSpacing="0.4px" sx={{ fontSize: "0.68rem" }}>
                          Overall Achievement %
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        fontWeight="800"
                        color={summary.overall_achievement_percentage >= 100 ? "#16a34a" : summary.overall_achievement_percentage >= 75 ? "#d97706" : "#dc2626"}
                        sx={{ fontSize: "1.1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", my: 0.5 }}
                      >
                        {Number(summary.total_target_amount) > 0 ? `${summary.overall_achievement_percentage || 0}%` : "—"}
                      </Typography>

                      <Typography variant="caption" fontWeight="500" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                        Total Orders: <b>{summary.total_actual_orders || 0}</b>
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Card 4: Status Breakdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 1.8,
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#ffffff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease",
                        "&:hover": { boxShadow: "0 6px 20px rgba(15,23,42,0.06)", borderColor: "#cbd5e1" },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.4}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: "#8b5cf6",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <GroupsIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography variant="caption" fontWeight="700" color="#64748b" textTransform="uppercase" letterSpacing="0.4px" sx={{ fontSize: "0.68rem" }}>
                          Status Breakdown
                        </Typography>
                      </Box>

                      <Grid container spacing={0.6} my={0.4}>
                        <Grid item xs={4}>
                          <Box textAlign="center" sx={{ bgcolor: "#f0fdf4", py: 0.3, px: 0.2, borderRadius: 1.5, border: "1px solid #bbf7d0" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#16a34a", fontSize: "0.8rem", lineHeight: 1.1 }}>
                              {summary.achieved_count || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#15803d", fontSize: "0.58rem", fontWeight: 700, display: "block", whiteSpace: "nowrap" }}>
                              Achieved
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box textAlign="center" sx={{ bgcolor: "#fefce8", py: 0.3, px: 0.2, borderRadius: 1.5, border: "1px solid #fef08a" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#ca8a04", fontSize: "0.8rem", lineHeight: 1.1 }}>
                              {summary.on_track_count || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#a16207", fontSize: "0.58rem", fontWeight: 700, display: "block", whiteSpace: "nowrap" }}>
                              On Track
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box textAlign="center" sx={{ bgcolor: "#fff5f5", py: 0.3, px: 0.2, borderRadius: 1.5, border: "1px solid #fecaca" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#dc2626", fontSize: "0.8rem", lineHeight: 1.1 }}>
                              {summary.lagging_count || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#b91c1c", fontSize: "0.58rem", fontWeight: 700, display: "block", whiteSpace: "nowrap" }}>
                              Lagging
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ fontSize: "0.7rem" }}>
                        <Typography variant="caption" fontWeight="500" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                          No target set: <b>{summary.no_target_count || 0}</b>
                        </Typography>
                        <Typography variant="caption" fontWeight="700" color="#0f172a" sx={{ fontSize: "0.72rem" }}>
                          Total: {summary.total_sales_persons || 0}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                {/* Table */}
                <Grid className="form_design" sx={{ mt: 2 }}>
                  <MaterialReactTable
                    columns={columnsMonthly}
                    data={reportData?.report || []}
                    state={{ isLoading: false }}
                    initialState={{ pagination: { pageSize: 100, pageIndex: 0 } }}
                    enableColumnActions={false}
                    enableRowSelection={false}
                    enableFullScreenToggle={false}
                    enableStickyHeader
                    className="mat-react-tbl"
                    layoutMode="grid"
                    renderTopToolbarCustomActions={({ table }) => (
                      <Button
                        onClick={() => exportMonthlyReportToExcel(table)}
                        startIcon={<FileDownloadIcon />}
                        sx={{ background: "#1a73e829", textTransform: "none" }}
                      >
                        Export To Excel
                      </Button>
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Tab 1: Month-wise Detailed Breakdown Matrix */}
            {activeTab === 1 && (
              <Box>
                {monthwiseData.map((sp) => (
                  <Paper
                    key={sp.sales_person_id}
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3.5,
                      border: "1px solid #e2e8f0",
                      borderRadius: 3,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, pb: 2, borderBottom: "1px solid #f1f5f9" }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: "#3b82f6", color: "#ffffff", fontWeight: 800, width: 46, height: 46, fontSize: "1.1rem" }}>
                          {sp.employee_name?.charAt(0) || "S"}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                            {sp.employee_name} ({sp.employee_no || "N/A"})
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Position: {sp.position_name || "Sales Executive"} | Year: {sp.year}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={2.5} alignItems="center">
                        <Box textAlign="right">
                          <Typography variant="caption" color="text.secondary" display="block">
                            Full Year YTD Target: <b style={{ color: "#0f172a" }}>{formatAED(sp.ytd_target_amount)}</b>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Full Year YTD Actual: <b style={{ color: "#0284c7" }}>{formatAED(sp.ytd_actual_sales)}</b>
                          </Typography>
                        </Box>
                        <Chip
                          label={Number(sp.ytd_target_amount) > 0 ? `YTD Achievement: ${sp.ytd_achievement_percentage}%` : "YTD: No target"}
                          color={sp.ytd_achievement_percentage >= 100 ? "success" : sp.ytd_achievement_percentage >= 75 ? "warning" : "error"}
                          sx={{ fontWeight: 800, fontSize: "0.85rem", py: 2, px: 1, borderRadius: 2 }}
                        />
                      </Stack>
                    </Box>

                    {/* 12-Month Grid */}
                    <Grid container spacing={2}>
                      {sp.monthly_breakdown.map((mb) => (
                        <Grid item xs={12} sm={6} md={3} lg={2} key={mb.month}>
                          <Card
                            elevation={0}
                            sx={{
                              border: "1px solid",
                              borderColor: mb.achievement_percentage >= 100 ? "#bbf7d0" : mb.achievement_percentage >= 75 ? "#fef08a" : "#fecaca",
                              bgcolor: mb.achievement_percentage >= 100 ? "#f0fdf4" : mb.achievement_percentage >= 75 ? "#fefce8" : "#fff5f5",
                              borderRadius: 2.5,
                              p: 2,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(0,0,0,0.06)" },
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}>
                              {mb.month_name} {sp.year}
                            </Typography>

                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
                              Target: <b>{formatAED(mb.target_amount)}</b>
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ fontWeight: 800, color: "#0284c7", mb: 1 }}>
                              Actual: {formatAED(mb.actual_sales)}
                            </Typography>

                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, mb.achievement_percentage)}
                              sx={{
                                my: 1,
                                display: Number(mb.target_amount) > 0 ? "block" : "none",
                                height: 7,
                                borderRadius: 4,
                                bgcolor: "#e2e8f0",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor: mb.achievement_percentage >= 100 ? "#16a34a" : mb.achievement_percentage >= 75 ? "#eab308" : "#dc2626",
                                  borderRadius: 4,
                                },
                              }}
                            />

                            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                              <Typography variant="caption" sx={{ fontWeight: 800, color: mb.achievement_percentage >= 100 ? "#16a34a" : mb.achievement_percentage >= 75 ? "#ca8a04" : "#dc2626" }}>
                                {Number(mb.target_amount) > 0 ? `${mb.achievement_percentage}%` : "—"}
                              </Typography>
                              <Chip
                                label={mb.target_source === "explicit" ? "Explicit" : mb.target_source === "inherited" ? "Past" : "Default"}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.6rem", height: 18, fontWeight: 700 }}
                              />
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default SalesTargetReport;
